// RevenueCat thin wrapper. Lazy-loads the SDK so the web bundle doesn't
// pull in the native Capacitor plugin code on routes that never paywall.
//
// Usage:
//   import { initRevenueCat, purchase, restore, getOfferings, currentTier } from "./revenuecat";
//   await initRevenueCat(liveUser.id);
//   const o = await getOfferings();
//   await purchase(o.current.availablePackages[0]);
//
// Entitlements expected (configure in RevenueCat dashboard):
//   "basic"      — 基本版 features
//   "pro"        — 專業版 features
//   "enterprise" — 旗艦版 features
//
// Products (auto-renewable subs, prices HKD; yearly = 10 months, i.e. 2 free):
//   pettobook_basic_monthly       HK$128 / month
//   pettobook_basic_yearly        HK$1,280 / year
//   pettobook_pro_monthly         HK$268 / month
//   pettobook_pro_yearly          HK$2,680 / year
//   pettobook_enterprise_monthly  HK$528 / month
//   pettobook_enterprise_yearly   HK$5,280 / year
// Prices mirror PLANS in src/App.jsx and create-subscription-checkout.

let _purchases = null;
let _configured = false;

// Returns { P }, never the plugin itself: resolving an async function with a
// Capacitor plugin proxy makes JS call proxy.then(), which Capacitor rejects
// ("Purchases.then() is not implemented on android").
async function loadSdk() {
  if (_purchases) return { P: _purchases };
  const isNative = typeof window !== "undefined"
    && !!window.Capacitor?.isNativePlatform?.();
  if (isNative) {
    const mod = await import("@revenuecat/purchases-capacitor");
    _purchases = mod.Purchases;
  } else {
    // Web Stripe path. Optional — only if you want to sell on web too.
    const mod = await import("@revenuecat/purchases-js");
    _purchases = mod.Purchases.configure
      ? mod.Purchases  // class-with-static (matches @revenuecat/purchases-js v0.x)
      : mod;
  }
  return { P: _purchases };
}

function platformApiKey() {
  const p = window.Capacitor?.getPlatform?.() || "web";
  if (p === "ios")     return import.meta.env.VITE_RC_APPLE_KEY;
  if (p === "android") return import.meta.env.VITE_RC_GOOGLE_KEY;
  return import.meta.env.VITE_RC_STRIPE_KEY;
}

export async function initRevenueCat(userId) {
  if (!userId) return;
  const apiKey = platformApiKey();
  if (!apiKey) {
    // eslint-disable-next-line no-console
    console.warn("[PettoBook] RevenueCat API key missing for this platform — skipping init.");
    return;
  }
  try {
    const { P } = await loadSdk();
    if (_configured) {
      // User changed (rare — re-login as different account). Re-identify so
      // entitlements reflect the new user.
      await P.logIn({ appUserID: userId });
      return;
    }
    await P.configure({ apiKey, appUserID: userId });
    _configured = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[PettoBook] RevenueCat init failed:", e);
  }
}

export async function getOfferings() {
  const { P } = await loadSdk();
  return P.getOfferings();
}

export async function purchase(pkg, changeInfo = null) {
  const { P } = await loadSdk();
  return P.purchasePackage(changeInfo
    ? { aPackage: pkg, storeProductChangeInfo: changeInfo }
    : { aPackage: pkg });
}

const TIER_RANK = { free: 0, basic: 1, pro: 2, enterprise: 3 };

// "pettobook_pro_monthly:monthly" → { tier: "pro", period: "monthly", subId: "pettobook_pro_monthly" }
export function parseProductId(id) {
  const m = /^pettobook_(basic|pro|enterprise)_(monthly|yearly)/.exec(id || "");
  return m ? { tier: m[1], period: m[2], subId: `pettobook_${m[1]}_${m[2]}` } : null;
}

// The user's current active PettoBook subscription as seen by RevenueCat
// (any store). { tier, period, subId, store, willRenew, expiresAt } or null.
export async function getActivePlan() {
  const { P } = await loadSdk();
  const res = await P.getCustomerInfo();
  const info = res?.customerInfo || res;
  let best = null;
  for (const ent of Object.values(info?.entitlements?.active || {})) {
    const p = parseProductId(ent.productIdentifier);
    if (!p) continue;
    if (!best || TIER_RANK[p.tier] > TIER_RANK[best.tier]) {
      best = { ...p, store: ent.store, willRenew: ent.willRenew, expiresAt: ent.expirationDate };
    }
  }
  return best;
}

// How Google Play should swap the old subscription for the new one.
//   higher tier          → immediate, charge prorated difference now
//   same tier, → yearly  → immediate, unused monthly time credited
//   lower tier / → monthly → at next renewal (keep what's paid for)
export function replacementModeFor(from, to) {
  if (TIER_RANK[to.tier] > TIER_RANK[from.tier]) return "CHARGE_PRORATED_PRICE";
  if (TIER_RANK[to.tier] < TIER_RANK[from.tier]) return "DEFERRED";
  return to.period === "yearly" ? "WITH_TIME_PRORATION" : "DEFERRED";
}

// Buys the Play/App Store product for a tier + period. RevenueCat package
// product ids on Google look like "pettobook_pro_monthly:monthly". If the
// user already has a Play subscription it is REPLACED (upgrade/downgrade),
// never stacked as a second subscription.
export async function purchaseTier(tier, period) {
  const offerings = await getOfferings();
  const prefix = `pettobook_${tier}_${period}`;
  const pkgs = Object.values(offerings?.all || {}).flatMap(o => o.availablePackages || []);
  const pkg = pkgs.find(p => (p.product?.identifier || "").startsWith(prefix));
  if (!pkg) throw new Error("暫時未有此方案，請稍後再試");
  const current = await getActivePlan().catch(() => null);
  let changeInfo = null;
  if (current) {
    if (current.subId === prefix) throw new Error("你已經訂閱緊呢個方案");
    if (current.store && current.store !== "PLAY_STORE") {
      throw new Error(current.store === "APP_STORE"
        ? "你嘅訂閱係經 Apple App Store 購買，請喺 iPhone 設定 → 訂閱 更改方案"
        : "你已有其他渠道嘅訂閱，請先取消再喺此購買");
    }
    changeInfo = {
      oldProductIdentifier: current.subId,
      replacementMode: replacementModeFor(current, { tier, period }),
    };
  }
  const result = await purchase(pkg, changeInfo);
  return { ...result, changeMode: changeInfo?.replacementMode || null };
}

// Google Play's own subscription page (cancel / payment method).
export function playManageUrl(subId) {
  const base = "https://play.google.com/store/account/subscriptions";
  return subId ? `${base}?sku=${encodeURIComponent(subId)}&package=com.pettobook.app` : base;
}

export function isNativeStore() {
  return typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
}

export async function restore() {
  const { P } = await loadSdk();
  return P.restorePurchases();
}

export async function getCustomerInfo() {
  const { P } = await loadSdk();
  return P.getCustomerInfo();
}

// Returns the effective tier based on a profile row. Trusts the
// server-side `subscription_*` columns (kept fresh by the webhook).
// The client SDK is used for PURCHASING, not for gating — that prevents a
// jailbroken device from spoofing entitlements client-side.
export function currentTier(profile) {
  if (!profile) return "free";
  const tier = profile.subscriptionTier || "free";
  if (tier === "free") return "free";
  const exp = profile.subscriptionExpiresAt
    ? new Date(profile.subscriptionExpiresAt).getTime()
    : 0;
  if (exp && exp < Date.now()) return "free";
  return tier;
}

export function isPro(profile) {
  const t = currentTier(profile);
  return t === "pro" || t === "enterprise";
}

export function isEnterprise(profile) {
  return currentTier(profile) === "enterprise";
}
